export type VendorScope = {
  columns: string[];
  resources: Record<string, string[]>;
};
export type VendorGrant = {
  appId: string;
  role: "viewer";
  products: string[];
  exportProducts: string[];
  dataScopes?: Record<string, VendorScope>;
};
export type VendorPerson = {
  signInMethod?: "password";
  email: string;
  name: string;
  status: "active" | "suspended";
  grants: VendorGrant[];
  requestedApps?: string[];
  identity?: { provider: "microsoft"; tenantId: string; objectId: string };
};
export type VendorProfile = {
  type: "agency" | "supplier" | "consultant" | "technology" | "other";
  purpose: string;
  ownerNotifications?: boolean;
  contractRef?: string;
  backupOwnerEmail?: string;
  supplierName?: string;
  operationsPortalId?: number;
  billComVendorId?: string;
};
export type VendorConfiguration = {
  name: string;
  ownerEmail: string;
  requestedByEmail?: string;
  expiresAt: string;
  members: VendorPerson[];
  profile?: VendorProfile;
};
export type VendorReview = {
  configurationHash: string;
  decisions: Record<
    string,
    {
      actor: string;
      decision: "approved" | "rejected";
      reason: string;
      at: string;
    }
  >;
};
export type Vendor = {
  vendorId: string;
  version: number;
  status: "draft" | "active" | "suspended" | "archived";
  published: VendorConfiguration | null;
  draft: VendorConfiguration | null;
  createdAt: string;
  createdBy?: string;
  updatedAt: string;
  updatedBy: string;
  draftReview?: VendorReview | null;
  publishedReview?: VendorReview | null;
  lastReviewedAt?: string;
};
export type VendorProduct = {
  id: string;
  appId: string;
  name: string;
  description: string;
  fields: string[];
  pii: string;
  minimumCohortSize: number;
  delivery?: "vendor-publication";
  scopeFields?: string[];
  resourceChoices?: Record<string, string[]>;
  vendorIds?: string[];
  sourceTime?: string;
  maxAgeHours?: number;
  ownerEmail?: string;
};
export type VendorApp = {
  id: string;
  name: string;
  url: string;
  roles: string[];
  owner: string;
  status: "available" | "integration_required" | "publication_required";
  description: string;
  delivery?: string;
  nativeUrl?: string;
};
export type VendorAudit = {
  recordId: string;
  action: string;
  actor: string;
  reason: string;
  occurredAt: string;
  version: number;
  before: unknown;
  after: unknown;
};
export type VendorPreset = {
  id: string;
  name: string;
  days: number;
  type: VendorProfile["type"];
  apps: string[];
  description: string;
};
export type VendorDirectory = {
  vendors: Vendor[];
  apps: VendorApp[];
  products: VendorProduct[];
  employees: { email: string; name: string }[];
  nextCursor: string | null;
  presets?: VendorPreset[];
};
export type VendorAccess = {
  active: boolean;
  vendorId: string;
  vendorName: string;
  ownerEmail: string;
  expiresAt: string;
  version: number;
  grants: VendorGrant[];
};

export async function vendorRequest<T>(
  base: string,
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const response = await fetch(`${base.replace(/\/$/, "")}/auth/${path}`, {
    ...init,
    credentials: "include",
    headers: { "Content-Type": "application/json", ...init.headers },
  });
  const body = await response.json().catch(() => null);
  if (!response.ok)
    throw new Error(
      body?.message || `Vendor access request failed (${response.status}).`,
    );
  return body as T;
}

export function effectiveVendorStatus(
  vendor: Vendor,
  now = Date.now(),
): string {
  if (
    vendor.status === "active" &&
    vendor.published &&
    Date.parse(vendor.published.expiresAt) <= now
  )
    return "expired";
  return vendor.status;
}

export function configurationChanges(
  before: VendorConfiguration | null,
  after: VendorConfiguration,
): string[] {
  const changes: string[] = [];
  if (!before)
    changes.push(
      `Activate ${after.name} with ${after.members.length} approved ${after.members.length === 1 ? "person" : "people"}.`,
    );
  else {
    if (before.name !== after.name)
      changes.push(`Vendor name: ${before.name} → ${after.name}.`);
    if (
      (before.requestedByEmail || before.ownerEmail) !==
      (after.requestedByEmail || after.ownerEmail)
    )
      changes.push(
        `Requester: ${before.requestedByEmail || before.ownerEmail} → ${after.requestedByEmail || after.ownerEmail}.`,
      );
    if (before.ownerEmail !== after.ownerEmail)
      changes.push(`Owner: ${before.ownerEmail} → ${after.ownerEmail}.`);
    if (before.expiresAt !== after.expiresAt)
      changes.push(
        `Expiry: ${before.expiresAt.slice(0, 10)} → ${after.expiresAt.slice(0, 10)}.`,
      );
  }
  const emails = new Set([
    ...(before?.members || []).map((person) => person.email),
    ...after.members.map((person) => person.email),
  ]);
  for (const email of emails) {
    const previous = before?.members.find((person) => person.email === email);
    const next = after.members.find((person) => person.email === email);
    if (!next) changes.push(`Revoke ${email}.`);
    else if (!previous)
      changes.push(
        `Add ${email}: ${next.grants.flatMap((grant) => grant.products).join(", ") || "no app access"}.`,
      );
    else if (JSON.stringify(previous) !== JSON.stringify(next))
      changes.push(
        `Update ${email}: ${next.status}, ${next.grants.flatMap((grant) => grant.products).join(", ") || "no app access"}, ${next.grants.flatMap((grant) => grant.exportProducts).length ? "exports allowed" : "exports disabled"}.`,
      );
  }
  for (const person of after.members) {
    const previous = before?.members.find(
      (item) => item.email === person.email,
    );
    if (
      JSON.stringify(previous?.requestedApps || []) !==
        JSON.stringify(person.requestedApps || []) &&
      person.requestedApps?.length
    )
      changes.push(
        `Requested apps for ${person.email}: ${person.requestedApps.join(", ")}. Requests do not grant access; only approved datasets listed above are activated.`,
      );
  }
  return changes.length
    ? changes
    : ["The saved configuration matches the current grant."];
}

export type VendorInvitation = {
  recordId: string;
  invitationId: string;
  email: string;
  subject: string;
  status:
    | "sending"
    | "accepted"
    | "delivered"
    | "bounced"
    | "complained"
    | "failed"
    | "delayed"
    | "delivery_unknown";
  vendorVersion: number;
  createdAt: string;
  updatedAt?: string;
  attempt?: number;
};
export type VendorInvitationPreview = {
  to: string;
  subject: string;
  html: string;
  text: string;
  replyTo: string;
  vendorVersion: number;
  draft: boolean;
};
export function invitationStatus(status: VendorInvitation["status"]): string {
  return {
    sending: "Sending — awaiting provider confirmation",
    accepted: "Accepted by email provider",
    delivered: "Delivered to recipient mail server",
    bounced: "Bounced",
    complained: "Reported as spam",
    failed: "Send failed",
    delayed: "Delivery delayed",
    delivery_unknown: "Delivery unknown — check receipts before retrying",
  }[status];
}

export type VendorActivity = {
  recordId: string;
  actor: string;
  action: string;
  occurredAt: string;
  appId?: string;
  productId?: string;
  rowCount?: number;
};
export type VendorOnboarding = {
  email: string;
  name: string;
  status: string;
  firstSignedInAt?: string;
  lastSignedInAt?: string;
  lastSeenAt?: string;
  identityProvider?: string;
};
export type VendorRenewal = {
  vendorId: string;
  name: string;
  ownerEmail: string;
  expiresAt: string;
  daysRemaining: number;
  ownerActive: boolean;
  reviewDue: boolean;
  version: number;
  status: string;
};
export type VendorAccessRequest = {
  requestId: string;
  version: number;
  configuration: VendorConfiguration;
  decisions: VendorReview["decisions"];
  status: "requested" | "approved" | "rejected" | "staged" | "cancelled";
  origin?: "vendor";
  requestedApps?: string[];
  requestedDatasets?: string[];
  preparedBy?: string;
  reason: string;
  createdAt: string;
  createdBy: string;
  vendorId?: string;
};
export type VendorInbox = {
  requests: VendorAccessRequest[];
  drafts: {
    vendorId: string;
    version: number;
    configuration: VendorConfiguration;
    review?: VendorReview;
    stages: string[];
  }[];
  apps: VendorApp[];
  products: VendorProduct[];
  presets: VendorPreset[];
  employees: VendorDirectory["employees"];
  renewals: VendorRenewal[];
  canAdminister: boolean;
  reviewableApps: string[];
  vendors?: { vendorId: string; name: string; status: string }[];
};
export function reviewStages(config: VendorConfiguration): string[] {
  return [
    "owner",
    ...[
      ...new Set(
        config.members.flatMap((person) => [
          ...person.grants.map((grant) => grant.appId),
          ...(person.requestedApps || []),
        ]),
      ),
    ]
      .sort()
      .map((app) => `app:${app}`),
  ];
}
export function reviewComplete(
  config: VendorConfiguration,
  review?: VendorReview | null,
): boolean {
  return reviewStages(config).every(
    (stage) => review?.decisions?.[stage]?.decision === "approved",
  );
}
export function stageLabel(stage: string, apps: VendorApp[]): string {
  return stage === "owner"
    ? "Internal owner"
    : `${apps.find((app) => app.id === stage.slice(4))?.name || stage.slice(4)} data access`;
}

export type VendorOwnerNotification = {
  notificationId: string;
  kind: string;
  to: string;
  status: string;
  createdAt: string;
  subject: string;
};
